import { useCallback, useEffect, useRef, useState } from "react";
import { takeBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import { MFG_BUILDER_ASSISTANT_REVIEW_ROLES, MFG_FINAL_QC_ROLE } from "@/lib/roles/turboManufacturingRoles";
import { readQcCorrection } from "@/lib/manufacturing/qcCorrection";
import {
  continueBuilderAssistantSession,
  getBuilderAssistantSession,
  listBuilderAssistantSessions,
  seedBuilderAssistant,
} from "@/lib/manufacturing/builderAssistantClient";
import type {
  BuilderAssistantBundle,
  BuilderAssistantComponent,
} from "@/lib/manufacturing/builderAssistantTypes";
import { BuilderAssistantChecklistEditor } from "./BuilderAssistantChecklistEditor";
import { BuilderAssistantChecklistWalk } from "./BuilderAssistantChecklistWalk";
import { BuilderAssistantFinalQc } from "./BuilderAssistantFinalQc";
import { BuilderAssistantCorrectionList } from "./BuilderAssistantCorrectionList";
import { BuilderAssistantFinish } from "./BuilderAssistantFinish";
import { BuilderAssistantHistory } from "./BuilderAssistantHistory";
import { BuilderAssistantQcReport } from "./BuilderAssistantQcReport";
import { BuilderAssistantLink } from "./BuilderAssistantLink";
import { BuilderAssistantReview } from "./BuilderAssistantReview";
import { BuilderAssistantStart } from "./BuilderAssistantStart";
import { BuilderAssistantWalk } from "./BuilderAssistantWalk";

type View = "inspect" | "review";
type Lane = "new" | "history" | "checklist";

function activityLines(summary: Record<string, unknown>, currentUserId: string | null) {
  const raw = summary.activity;
  if (!Array.isArray(raw)) return [];
  const lines: string[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const action = row.action === "continued" ? "Continued" : row.action === "edited" ? "Edited" : null;
    const at = typeof row.at === "string" ? row.at : "";
    const userId = typeof row.user_id === "string" ? row.user_id : "";
    if (!action || !at) continue;
    const who = currentUserId && userId === currentUserId ? "you" : "a teammate";
    const date = new Date(at);
    const when = Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
    lines.push(when ? `${action} by ${who} · ${when}` : `${action} by ${who}`);
  }
  return lines.slice(-6);
}

export function BuilderAssistantPanel() {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const canReview = auth.hasRole(...MFG_BUILDER_ASSISTANT_REVIEW_ROLES);
  const canFinalQc = auth.hasRole(MFG_FINAL_QC_ROLE);
  const currentUserId = auth.session?.user?.id ?? null;
  const [view, setView] = useState<View>("inspect");
  const [lane, setLane] = useState<Lane>("new");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [reportOnly, setReportOnly] = useState(false);
  const [focusCategoryId, setFocusCategoryId] = useState<string | null>(null);
  const [bundle, setBundle] = useState<BuilderAssistantBundle | null>(null);
  const [components, setComponents] = useState<BuilderAssistantComponent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [unsubmitted, setUnsubmitted] = useState(false);
  const flushChecks = useRef<(() => Promise<{ ok: true } | { ok: false; error: string }>) | null>(null);
  const reloadGen = useRef(0);

  const refreshOpenInspections = useCallback(async () => {
    const res = await listBuilderAssistantSessions();
    if (!res.ok) return;
    setUnsubmitted(res.data.sessions.some((row) => row.session_status !== "COMPLETED"));
  }, []);

  const reload = useCallback(async (id: string) => {
    const gen = reloadGen.current + 1;
    reloadGen.current = gen;
    const res = await getBuilderAssistantSession(id);
    if (gen !== reloadGen.current) return;
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setBundle(res.data);
    setComponents(res.data.components);
  }, []);

  async function resumeSession(sessionIdToOpen: string) {
    setError(null);
    setReportOnly(false);
    const res = await continueBuilderAssistantSession(sessionIdToOpen);
    if (!res.ok && !res.error.includes("already open")) {
      setError(res.error);
      return;
    }
    setView("inspect");
    setLane("history");
    setSessionId(sessionIdToOpen);
  }

  function openSession(sessionIdToOpen: string) {
    setReportOnly(false);
    setView("inspect");
    setLane("history");
    setSessionId(sessionIdToOpen);
  }

  useEffect(() => {
    function openPending() {
      const pending = takeBuilderAssistantSession();
      if (!pending) return;
      setView("inspect");
      setLane("history");
      setReportOnly(pending.reportOnly);
      setFocusCategoryId(pending.categoryId);
      setSessionId(pending.sessionId);
    }
    openPending();
    window.addEventListener("mfg-ba-pending-session", openPending);
    return () => window.removeEventListener("mfg-ba-pending-session", openPending);
  }, []);

  useEffect(() => {
    if (!gate.enabled) return;
    void seedBuilderAssistant().then((res) => {
      if (res.ok) setComponents(res.data.components);
    });
    void refreshOpenInspections();
  }, [gate.enabled, refreshOpenInspections]);

  useEffect(() => {
    if (!sessionId) return;
    void reload(sessionId);
  }, [sessionId, reload]);

  return (
    <div className="w-full space-y-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Builder Assistant</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Capture what you see. A unit match is optional until inventory has the building.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="tablist" aria-label="Inspections" className="inline-flex rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-800">
            {([
              ["new", "New"],
              ["history", "History"],
              ["checklist", "Checklist"],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={lane === id}
                className={
                  "rounded-md px-3 py-1 text-sm font-semibold " +
                  (lane === id
                    ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
                    : id === "history" && unsubmitted
                      ? "text-red-700 ring-1 ring-red-500 dark:text-red-300"
                      : "text-gray-600 dark:text-zinc-400")
                }
                onClick={() => {
                  setLane(id);
                  setReportOnly(false);
                  setSessionId(null);
                  setBundle(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        {canReview ? (
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => setView((current) => (current === "review" ? "inspect" : "review"))}
          >
            {view === "review" ? "Back to inspection" : "Review candidates"}
          </button>
        ) : null}
        </div>
      </div>
      <ManufacturingGateBanner gate={gate} />
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {!gate.enabled ? null : reportOnly && sessionId ? (
        <BuilderAssistantQcReport
          sessionId={sessionId}
          onClose={() => {
            setReportOnly(false);
            setSessionId(null);
            setBundle(null);
          }}
        />
      ) : view === "review" && canReview ? (
        <BuilderAssistantReview
          components={components}
          onComponentsChanged={() => {
            void seedBuilderAssistant().then((res) => {
              if (res.ok) setComponents(res.data.components);
            });
            if (sessionId) void reload(sessionId);
          }}
        />
      ) : (
        <>
          {!sessionId && lane === "new" ? (
            <BuilderAssistantStart
              onStarted={(id) => {
                setSessionId(id);
                setUnsubmitted(true);
              }}
            />
          ) : null}
          {!sessionId && lane === "history" ? (
            <BuilderAssistantHistory
              onOpen={openSession}
              onResume={(id) => void resumeSession(id)}
              onDeleted={(id) => {
                if (sessionId === id) {
                  setSessionId(null);
                  setBundle(null);
                }
              }}
            />
          ) : null}
          {!sessionId && lane === "checklist" ? <BuilderAssistantChecklistEditor /> : null}
          {bundle ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-gray-800 dark:text-zinc-200">
                  {bundle.session.serial_text ? `Serial ${bundle.session.serial_text}` : "No serial yet"}
                  {bundle.session.model_hint ? ` · ${bundle.session.model_hint}` : ""}
                  {bundle.session.mfg_unit_id ? "" : " · not linked to a unit"}
                </p>
                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() => {
                    setSessionId(null);
                    setBundle(null);
                    void refreshOpenInspections();
                  }}
                >
                  Close
                </button>
              </div>
              {activityLines(bundle.session.session_summary, currentUserId).length > 0 ? (
                <ul className="text-xs text-gray-500 dark:text-zinc-400">
                  {activityLines(bundle.session.session_summary, currentUserId).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : null}
              <BuilderAssistantLink
                session={bundle.session}
                onChanged={() => void reload(bundle.session.id)}
              />
              <BuilderAssistantWalk
                bundle={bundle}
                assumedFixerName={(auth.profile?.display_name || auth.profile?.full_name || "").trim()}
                onChanged={() => void reload(bundle.session.id)}
              />
              {canFinalQc && bundle.session.session_status === "COMPLETED" && !readQcCorrection(bundle.session.session_summary) ? (
                <BuilderAssistantFinalQc
                  sessionId={bundle.session.id}
                  summary={bundle.session.session_summary}
                  observations={bundle.observations}
                  onChanged={() => reload(bundle.session.id)}
                />
              ) : readQcCorrection(bundle.session.session_summary) ? (
                <BuilderAssistantCorrectionList
                  sessionId={bundle.session.id}
                  lines={readQcCorrection(bundle.session.session_summary)?.lines ?? []}
                  disabled={bundle.session.session_status === "COMPLETED"}
                  onChanged={() => void reload(bundle.session.id)}
                  onFlushPending={(flush) => {
                    flushChecks.current = flush;
                  }}
                />
              ) : (
              <BuilderAssistantChecklistWalk
                sessionId={bundle.session.id}
                observations={bundle.observations}
                onOpenSession={openSession}
                disabled={bundle.session.session_status === "COMPLETED"}
                focusCategoryId={focusCategoryId}
                onChanged={() => reload(bundle.session.id)}
                photographedObservationIds={bundle.evidence
                  .filter((photo) => photo.caption !== "fix")
                  .map((photo) => photo.observation_id)}
                evidenceStamp={bundle.evidence.map((photo) => photo.id).join(",")}
                onFlushPending={(flush) => {
                  flushChecks.current = flush;
                }}
              />
              )}
              <BuilderAssistantFinish
                sessionId={bundle.session.id}
                summary={bundle.summary}
                sessionSummary={bundle.session.session_summary}
                completed={bundle.session.session_status === "COMPLETED"}
                onBeforeSubmit={() => flushChecks.current?.() ?? Promise.resolve({ ok: true as const })}
                onSubmitted={() => {
                  setSessionId(null);
                  setBundle(null);
                  setLane("history");
                  void refreshOpenInspections();
                }}
                onContinued={() => void reload(bundle.session.id)}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
